import { components } from "@appretio/api";
import { type Option } from "@appretio/ui-kit";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import { MODEL_OPTIONS } from "../../utils/algorithmicProducer";
import { getDisplayLabel } from "../../utils/summary/summaryPageHelpers";

/** SPEC: "no default beyond the first entry". */
export const DEFAULT_MODELS = MODEL_OPTIONS.slice(0, 1).map(
  (option) => option.value
);

/**
 * Planner-facing copy per failure reason. The schema is explicit that the
 * frontend switches on `reason`, not on prose; `summarizeGenerateOutputs`
 * appends `detail` only for the reasons whose detail is planner copy. Typed
 * on the enum so a new reason fails the build here instead of reaching
 * planners unmapped.
 */
export const FORECAST_ERROR_COPY: Record<
  components["schemas"]["ForecastErrorReason"],
  string
> = {
  insufficient_history:
    "not enough history in the training range for this model",
  invalid_history:
    "the training range has weeks with no data — try a later training start",
  fit_failed: "the model could not fit this series",
  model_unavailable: "this model is not available yet"
};

const FORECASTABLE_KPIS: components["schemas"]["ForecastableKpi"][] = [
  "GIS1",
  "GIR",
  "GS1",
  "GS1_COGS",
  "GS2",
  "GR2",
  "COGR"
];

/** Labels each offerable KPI via the tenant's own `metricDisplayMapping`, matching the grid row it produces. */
export function buildKpiOptions(
  metaData: ForecastMetaDataType | undefined
): Option[] {
  return FORECASTABLE_KPIS.map((kpi) => ({
    label: getDisplayLabel(kpi, metaData),
    value: kpi
  }));
}

export const DEFAULT_KPIS: components["schemas"]["ForecastableKpi"][] = [
  "GIS1"
];

/** Narrows a MultiSelect's string values back to the wire KPI union without a cast. */
export function isForecastableKpi(
  value: string
): value is components["schemas"]["ForecastableKpi"] {
  return FORECASTABLE_KPIS.some((kpi) => kpi === value);
}

/** One lookup for every select — string values from onChange, numeric ones from the week defaults. */
export function findOption(
  options: Option[],
  value: string | number | undefined
): Option | undefined {
  return options.find((option) => option.value === String(value));
}
